import { useState, useCallback, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ExportService } from "@/services/export.service";

export type ExportType = "orders" | "financial" | "abandoned-carts" | "physical-orders" | "subscriptions";
export type ExportStatus = "idle" | "starting" | "pending" | "completed" | "failed";

export interface ExportState {
  status: ExportStatus;
  reportId: string | null;
  downloadUrl: string | null;
  errorMessage: string | null;
}

export interface ExportFilters {
  [key: string]: any;
}

export interface ExportResponse {
  report_id: string;
  type: string;
  status: string;
}

export interface ExportStatusResponse {
  id: string;
  type: string;
  status: ExportStatus;
  created_at: string;
  download_url?: string;
  expires_at?: string;
  error_message?: string;
}

const RATE_LIMIT_MS = 60 * 1000;
const POLLING_INTERVAL = 2000;
const TIMEOUT = 5 * 60 * 1000;

const getLastExportKey = (type: ExportType) => `export-${type}-last-time`;

export const canStartExport = (type: ExportType): boolean => {
  const key = getLastExportKey(type);
  const lastExportTime = localStorage.getItem(key);
  if (!lastExportTime) return true;

  const timeSinceLastExport = Date.now() - parseInt(lastExportTime, 10);
  return timeSinceLastExport >= RATE_LIMIT_MS;
};

export const getRemainingCooldownTime = (type: ExportType): number => {
  const key = getLastExportKey(type);
  const lastExportTime = localStorage.getItem(key);
  if (!lastExportTime) return 0;

  const timeSinceLastExport = Date.now() - parseInt(lastExportTime, 10);
  const remaining = RATE_LIMIT_MS - timeSinceLastExport;
  return Math.max(0, Math.ceil(remaining / 1000));
};

export function useExport(type: ExportType) {
  const { t } = useTranslation(["sales", "common"]);
  const [state, setState] = useState<ExportState>({
    status: "idle",
    reportId: null,
    downloadUrl: null,
    errorMessage: null,
  });

  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const cleanup = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const checkStatus = useCallback(async (reportId: string) => {
    try {
      const data = await ExportService.checkExportStatus(reportId);

      setState(prev => ({
        ...prev,
        status: data.status,
        downloadUrl: data.download_url || null,
        errorMessage: data.error_message || null,
      }));

      if (data.status === "completed" || data.status === "failed") {
        cleanup();
      }
    } catch (error: any) {
      cleanup();
      setState(prev => ({
        ...prev,
        status: "failed" as const,
        errorMessage: error.message || t("sales:export_modal.failed"),
      }));
    }
  }, [cleanup, t]);

  const startExport = useCallback(async (filters: ExportFilters = {}) => {
    setState({
      status: "starting",
      reportId: null,
      downloadUrl: null,
      errorMessage: null,
    });

    try {
      const response = await ExportService.startExport(type, filters);

      const key = getLastExportKey(type);
      localStorage.setItem(key, Date.now().toString());

      setState(prev => ({
        ...prev,
        status: "pending",
        reportId: response.report_id,
      }));

      pollingRef.current = setInterval(() => {
        checkStatus(response.report_id);
      }, POLLING_INTERVAL);

      timeoutRef.current = setTimeout(() => {
        cleanup();
        setState(prev => ({
          ...prev,
          status: "failed" as const,
          errorMessage: t("sales:export_modal.export_timeout"),
        }));
      }, TIMEOUT);

    } catch (error: any) {
      setState(prev => ({
        ...prev,
        status: "failed" as const,
        errorMessage: error.message || t("sales:export_modal.export_start_error"),
      }));
    }
  }, [type, checkStatus, cleanup, t]);

  const cancelExport = useCallback(async (reason?: string) => {
    if (!state.reportId) return;

    try {
      await ExportService.cancelExport(state.reportId, reason);
      cleanup();

      setState({
        status: "idle",
        reportId: null,
        downloadUrl: null,
        errorMessage: null,
      });
    } catch (error: any) {
      console.error("Cancel export error:", error);
    }
  }, [state.reportId, cleanup]);

  const downloadFile = useCallback(() => {
    if (state.downloadUrl) {
      window.open(state.downloadUrl, "_blank");
    }
  }, [state.downloadUrl]);

  const reset = useCallback(() => {
    cleanup();
    setState({
      status: "idle",
      reportId: null,
      downloadUrl: null,
      errorMessage: null,
    });
  }, [cleanup]);

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  return {
    ...state,
    startExport,
    cancelExport,
    downloadFile,
    reset,
    canStart: canStartExport(type),
    remainingTime: getRemainingCooldownTime(type),
  };
}
